import type {RuleSet} from 'styled-components';

import {css} from 'styled-components';

import type {WithThemeProp} from '../../styles';
import type {StyledProps, Variant, Weight} from './types';

import {getColor, getRem} from '../../styles';
import {platform} from '../../utils/platform';

const getFontWeightStyle =
  (weight: Weight) =>
  <ComponentProps extends WithThemeProp>(p: ComponentProps) => {
    if (platform.isNative) {
      return css`
        font-family: ${() => {
          switch (weight) {
            case 'bold':
              return p.theme.font.bold;
            case 'regular':
              return p.theme.font.regular;
            case 'semibold':
              return p.theme.font.semibold;
            default:
              return p.theme.font.regular;
          }
        }};
      `;
    }

    return css<StyledProps>`
      font-weight: ${() => {
        switch (weight) {
          case 'bold':
            return p.theme.fontWeight.bold;
          case 'regular':
            return p.theme.fontWeight.regular;
          case 'semibold':
            return p.theme.fontWeight.semibold;
          default:
            return p.theme.fontWeight.regular;
        }
      }};
    `;
  };

const variants: Record<Variant, RuleSet<object>> = {
  body: css`
    font-size: ${getRem(14)};
    line-height: ${getRem(20)};
  `,
  h1: css`
    font-size: ${getRem(36)};
    line-height: ${getRem(48)};
    ${getFontWeightStyle('bold')};
  `,
  h2: css`
    font-size: ${getRem(24)};
    line-height: ${getRem(32)};
  `,
  h3: css`
    font-size: ${getRem(20)};
    line-height: ${getRem(28)};
  `,
  'label.la': css`
    font-size: ${getRem(12)};
    line-height: ${getRem(16)};
  `,
  'label.sm': css`
    font-size: ${getRem(10)};
    line-height: ${getRem(12)};
  `,
  subtitle: css`
    font-size: ${getRem(16)};
    line-height: ${getRem(24)};
  `,
};

/**
 * Basic shared css for any renderer.
 */
export const baseTextStyles = css<Required<StyledProps>>`
  box-sizing: border-box;
  color: ${p => getColor(p.color || 'text.black')(p)};

  ${p => variants[p.variant || 'body']};

  ${p => p.weight && getFontWeightStyle(p.weight)(p)};
`;

/**
 * Final css for the dom renderer
 */
export const web = css<StyledProps>`
  ${baseTextStyles};
`;

/**
 * Final css for the native renderer
 */
export const native = css<StyledProps>`
  ${baseTextStyles};
`;
